import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { global } from '../global';
import { ConnectionStatusService } from './connection-status.service';

export interface MetaApiPrice {
  symbol: string;
  bid: number;
  ask: number;
  cotizacion: number;
  time: string;
  /** Hora del broker (`YYYY-MM-DD HH:mm:ss.SSS`); el backend la usa para fechar la vela. */
  brokerTime?: string;
  /** Valor de un tick para 1 lote, en la divisa de la cuenta, en ganancia / en pérdida (MetaTrader) */
  profitTickValue?: number;
  lossTickValue?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MetaApiRealTimeWsService {
  private socket$: WebSocketSubject<any>;
  private priceSubject: Subject<MetaApiPrice> = new Subject();
  private isConnectedSubject = new BehaviorSubject<boolean>(false);
  private reconnectionAttempts = 0;
  private readonly RECONNECT_INTERVAL = 5000;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private currentIsos: string[] = [];
  private gaveUp = false;

  constructor(private connectionStatusService: ConnectionStatusService) {
    this.connectionStatusService.isBackendDown$.subscribe((isDown) => {
      if (!isDown && this.gaveUp && this.currentIsos.length > 0) {
        this.gaveUp = false;
        this.reconnectionAttempts = 0;
        this.establishConnection(this.currentIsos);
      }
    });
  }

  public connect(isos: string[]): void {
    if (!isos || isos.length === 0) return;
    this.currentIsos = isos;
    this.reconnectionAttempts = 0;
    this.gaveUp = false;
    this.establishConnection(isos);
  }

  private establishConnection(isos: string[]): void {
    const url = `${global.wsUrl}ws/tiemporeal?isos=${isos.map(encodeURIComponent).join(',')}`;
    if (this.socket$ && !this.socket$.closed) {
      this.socket$.complete();
    }
    this.socket$ = webSocket(url);
    this.socket$.subscribe({
      next: (data: MetaApiPrice) => {
        this.isConnectedSubject.next(true);
        this.priceSubject.next(data);
        localStorage.setItem(`nec_rt_${data.symbol}`, JSON.stringify({ ...data, _ts: Date.now() }));
      },
      error: () => {
        this.isConnectedSubject.next(false);
        this.reconnect();
      },
      complete: () => {
        this.isConnectedSubject.next(false);
      }
    });
  }

  private reconnect(): void {
    this.reconnectionAttempts++;
    if (this.reconnectionAttempts < this.MAX_RECONNECT_ATTEMPTS) {
      setTimeout(() => {
        if (this.currentIsos.length > 0) {
          this.establishConnection(this.currentIsos);
        }
      }, this.RECONNECT_INTERVAL);
    } else {
      this.gaveUp = true;
      this.connectionStatusService.reportFailure();
    }
  }

  public disconnect(): void {
    this.currentIsos = [];
    this.gaveUp = false;
    this.reconnectionAttempts = this.MAX_RECONNECT_ATTEMPTS; // prevent reconnect
    if (this.socket$) {
      this.socket$.complete();
    }
    this.isConnectedSubject.next(false);
  }

  public getPriceUpdates(): Observable<MetaApiPrice> {
    return this.priceSubject.asObservable();
  }

  public pollLocalPrices(isos: string[], intervalMs = 2000): Observable<MetaApiPrice> {
    return new Observable(observer => {
      const seen = new Map<string, number>();
      const id = setInterval(() => {
        isos.forEach(iso => {
          const raw = localStorage.getItem(`nec_rt_${iso}`);
          if (!raw) return;
          try {
            const price = JSON.parse(raw) as MetaApiPrice & { _ts: number };
            const stale = Date.now() - price._ts > 30_000;
            const alreadySeen = seen.get(iso) === price._ts;
            if (!stale && !alreadySeen) {
              seen.set(iso, price._ts);
              observer.next(price);
            }
          } catch {}
        });
      }, intervalMs);
      return () => clearInterval(id);
    });
  }

  public isConnected(): Observable<boolean> {
    return this.isConnectedSubject.asObservable();
  }
}
